<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1>
          <font-awesome-icon icon="plug" />
          {{ $t('socialWebhookLog') }}
        </h1>
        <p>{{ $t('socialWebhookLogDesc') }}</p>
      </div>
    </div>

    <div class="row">
      <!-- Tabs theo channel -->
      <div class="col-12">
        <b-tabs v-model="channelTabIndex" @input="onChannelChange">
          <b-tab :title="$t('allChannels')"></b-tab>
          <b-tab v-for="t in channelTabs" :key="t.value">
            <template #title>
              <span class="swl-tab-dot" :style="{ background: CHANNEL_COLORS[t.value] }"></span>
              {{ t.text }}
            </template>
          </b-tab>
        </b-tabs>
      </div>

      <!-- Filter -->
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <b-form-input class="mr-1 mb-1" v-model="filterFormModel.keyword" v-on:keydown.enter="search"
                          :placeholder="$t('keywordPlaceholder')" style="min-width: 240px"></b-form-input>
            <client-select :model="filterFormModel" attribute="client" @change="search" :isFilter="true"
                           :place-holder="$t('selectClient')" class="mr-1 mb-1"></client-select>
            <b-form-select class="mr-1 mb-1" v-model="filterFormModel.status" :options="[
                  {value: null, text: $t('selectStatus')},
                  {value: 'success', text: $t('success')},
                  {value: 'error', text: $t('error')},
                ]"></b-form-select>
            <b-form-checkbox class="mr-2 mb-1" v-model="filterFormModel.unresolved" value="1" unchecked-value="">
              {{ $t('showOnlyUnresolved') }}
            </b-form-checkbox>
            <time-range-picker :model="filterFormModel" :defaultShortcut="0" :label="$t('createTime')"
                               class="mr-1 mb-1"></time-range-picker>
            <button @click="search" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
            <button @click="resetFilter" class="btn btn-secondary mr-1 mb-1">{{ $t('reset') }}</button>
          </div>
        </div>
      </div>

      <!-- Paging top -->
      <div class="col-12">
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="itemCount">
            {{ $t('found', [itemCount]) }} · <span class="text-muted">{{ $t('retentionNote') }}</span>
          </div>
          <div class="c-paging__right">
            <div class="c-paging__numbers">
              <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                            v-model="filterFormModel.page" :total-rows="itemCount"
                            :per-page="filterFormModel.pageSize" @input="load"></b-pagination>
            </div>
          </div>
        </div>
      </div>

      <!-- Card list — dễ scan hơn smart-table -->
      <div class="col-12" v-if="items.length">
        <div class="swl-list">
          <div v-for="item in items" :key="item._id"
               class="swl-card" :class="['swl-card--' + item.status]"
               :style="{ borderLeftColor: CHANNEL_COLORS[item.channel] || '#888' }"
               @click="openDetail(item)" role="button">
            <!-- Cột trái: channel + time -->
            <div class="swl-card__left">
              <span class="swl-channel-pill" :style="{ background: CHANNEL_COLORS[item.channel] || '#888' }">
                {{ CHANNEL_LABELS[item.channel] || item.channel }}
              </span>
              <div class="swl-card__time">
                <div>{{ item.createTime | moment('DD/MM HH:mm:ss') }}</div>
                <div class="swl-meta">{{ item.durationMs }}ms<span v-if="item.sampled"> · sample</span></div>
              </div>
            </div>

            <!-- Cột giữa: account / client / URL -->
            <div class="swl-card__main">
              <div class="swl-row-1">
                <span v-if="item.account" class="swl-account">
                  <font-awesome-icon icon="user-circle" class="text-muted mr-1" />
                  <strong>{{ item.account.name }}</strong>
                  <span class="swl-meta ml-2">{{ accountIdShort(item.account) }}</span>
                </span>
                <span v-else-if="item.unresolved" class="badge badge-warning">{{ $t('unresolved') }}</span>
                <span v-else class="text-muted">—</span>

                <span v-if="item.client" class="ml-3 swl-client">
                  <font-awesome-icon icon="building" class="text-muted mr-1" />
                  {{ item.client.domain }}
                </span>
              </div>
              <div class="swl-row-2">
                <code class="swl-url">{{ item.method }} {{ item.url }}</code>
              </div>
              <div v-if="item.errorMessage" class="swl-row-3 text-danger small">
                <font-awesome-icon icon="exclamation-triangle" class="mr-1" />
                {{ item.errorMessage }}
              </div>
            </div>

            <!-- Cột phải: HTTP status -->
            <div class="swl-card__right">
              <div class="swl-http"
                   :class="item.status === 'success' ? 'swl-http--ok' : 'swl-http--err'">
                {{ item.resStatus || 'ERR' }}
              </div>
              <div class="swl-meta text-center">{{ item.status === 'success' ? $t('success') : $t('error') }}</div>
            </div>

            <!-- Chevron mở chi tiết -->
            <div class="swl-card__chev">
              <font-awesome-icon icon="chevron-right" />
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 text-center text-danger" v-if="!items.length && !listLoading">
        <span>{{ $t('notFoundSearch') }}</span>
      </div>

      <!-- Paging bottom -->
      <div class="col-12" v-if="itemCount">
        <div class="c-paging clearfix">
          <div class="c-paging__count">{{ $t('found', [itemCount]) }}</div>
          <div class="c-paging__right">
            <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                          v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                          @input="load">
            </b-pagination>
          </div>
        </div>
      </div>
    </div>

    <detail-modal ref="detailModal" :channelColors="CHANNEL_COLORS" :channelLabels="CHANNEL_LABELS"></detail-modal>
  </main>
</template>

<script>
import List from '@/mixins/List';
import TimeRangePicker from '@/components/TimeRangePicker';
import ClientSelect from '@/components/ClientSelect.vue';
import DetailModal from './Detail.vue';
import moment from 'moment';

// Màu brand từng channel (chuẩn nhận diện)
const CHANNEL_COLORS = {
  facebook: '#1877F2',
  instagram: '#E1306C',
  whatsapp: '#25D366',
  'zalo-oa': '#0068FF',
  telegram: '#0088CC',
  viber: '#7360F2',
  pancake: '#F47A40',
  tiktok: '#010101'
};

const CHANNEL_LABELS = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  whatsapp: 'WhatsApp',
  'zalo-oa': 'Zalo OA',
  telegram: 'Telegram',
  viber: 'Viber',
  pancake: 'Pancake',
  tiktok: 'TikTok'
};

const CHANNEL_KEYS = Object.keys(CHANNEL_LABELS);

export default {
  components: { ClientSelect, TimeRangePicker, DetailModal },
  mixins: [List],
  data() {
    return {
      service: '/social-webhook-log',
      autoLoad: true,
      channelTabIndex: 0,
      CHANNEL_COLORS,
      CHANNEL_LABELS,
      defaultFilterFormModel: {
        page: 1,
        pageSize: 50,
        channel: null,
        client: null,
        status: null,
        unresolved: '',
        keyword: null,
        from: moment().startOf('day').valueOf(),
        to: moment().endOf('day').valueOf()
      },
      channelTabs: CHANNEL_KEYS.map(k => ({ value: k, text: CHANNEL_LABELS[k] }))
    }
  },
  methods: {
    onChannelChange(idx) {
      this.filterFormModel.channel = idx === 0 ? null : CHANNEL_KEYS[idx - 1];
      this.search();
    },
    search() {
      this.filterFormModel.page = 1;
      this.load();
    },
    resetFilter() {
      this.filterFormModel = Object.assign({}, this.defaultFilterFormModel);
      this.channelTabIndex = 0;
      this.load();
    },
    accountIdShort(acc) {
      if (!acc) return '';
      const id = acc.facebookPageId || acc.instagramId || acc.whatsappNumberId || acc.oaId || acc.tiktokId || acc.zaloId;
      return id ? id : '';
    },
    openDetail(item) {
      this.$refs.detailModal.open(item._id);
    }
  },
  i18n: {
    messages: {
      en: {
        socialWebhookLog: 'Social webhook log',
        socialWebhookLogDesc: 'Logs of incoming social network webhooks. Retained for 3 days.',
        allChannels: 'All',
        selectStatus: 'All statuses',
        selectClient: 'Select client',
        unresolved: 'Unresolved',
        showOnlyUnresolved: 'Only unresolved',
        keywordPlaceholder: 'Search URL / error',
        retentionNote: 'Retained for 3 days',
        reset: 'Reset',
        success: 'Success',
        error: 'Error'
      },
      vi: {
        socialWebhookLog: 'Nhật ký webhook MXH',
        socialWebhookLogDesc: 'Nhật ký request webhook MXH gửi vào hệ thống. Lưu trong 3 ngày.',
        allChannels: 'Tất cả',
        selectStatus: 'Tất cả trạng thái',
        selectClient: 'Chọn khách hàng',
        unresolved: 'Chưa map account',
        showOnlyUnresolved: 'Chỉ xem chưa map',
        keywordPlaceholder: 'Tìm URL / lỗi',
        retentionNote: 'Lưu 3 ngày',
        reset: 'Đặt lại',
        success: 'OK',
        error: 'Lỗi'
      }
    }
  }
}
</script>

<style scoped>
.swl-tab-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}

.swl-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.swl-card {
  display: flex;
  align-items: stretch;
  background: #fff;
  border: 1px solid #e8eaed;
  border-left: 4px solid #888;
  border-radius: 6px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.15s;
  gap: 16px;
}
.swl-card:hover {
  border-color: #c9d1d9;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  transform: translateY(-1px);
}
.swl-card--error {
  background: #fef6f6;
}

.swl-card__left {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 130px;
  flex-shrink: 0;
}

.swl-channel-pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.swl-card__time {
  font-size: 13px;
  color: #333;
}

.swl-meta {
  font-size: 11px;
  color: #888;
}

.swl-card__main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.swl-row-1 {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.swl-account strong {
  color: #222;
}
.swl-client {
  color: #555;
  font-size: 13px;
}

.swl-row-2 {
  font-size: 12px;
}
.swl-url {
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: #f6f8fa;
  padding: 2px 6px;
  border-radius: 3px;
  color: #444;
}

.swl-row-3 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.swl-card__right {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 70px;
  flex-shrink: 0;
}
.swl-http {
  font-size: 18px;
  font-weight: 700;
  font-family: 'Menlo', 'Monaco', monospace;
  line-height: 1.2;
}
.swl-http--ok { color: #28a745; }
.swl-http--err { color: #dc3545; }

.swl-card__chev {
  display: flex;
  align-items: center;
  color: #bbb;
  flex-shrink: 0;
}
</style>
